import { memo, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import merge from 'deepmerge';
import debounce from 'lodash/debounce';

export const getWebIDHashed = () => {
    if (sessionStorage.getItem('customerHash') && sessionStorage.getItem('customerHash') !== 'undefined') {
        return sessionStorage.getItem('customerHash');
    }

    if (sessionStorage.getItem('lite_account_id') && sessionStorage.getItem('lite_account_id') !== 'undefined') {
        return sessionStorage.getItem('lite_account_id');
    }
};

export const getCustomerSegment = () => {
    if (sessionStorage.getItem('customerSegment') && sessionStorage.getItem('customerSegment') !== 'undefined') {
        return sessionStorage.getItem('customerSegment');
    }
};

export const getPCardTier = () => {
    if (sessionStorage.getItem('PCardTier') && sessionStorage.getItem('PCardTier') !== 'undefined') {
        return sessionStorage.getItem('PCardTier');
    }
};

const ewin2NonProdDomains = [
    'ewin2bet.iuww.com',
    'bet.qbww.com',
    'bet2nd.qbww.com',
    'cuasb2.cuww.com',
    'cuasb22nd.cuww.com'
];

export const getPageId = () => {
    const isMassSite = !window.globalConfig.IS_SPEEDBET && !window.globalConfig.IS_EWIN2;
    const isSpeedBetSite = window.globalConfig.IS_SPEEDBET;
    const isHkjcFootballSite =
        window.globalConfig.IS_EWIN2 &&
        (ewin2NonProdDomains.includes(window.location.host) || window.location.host === 'betslip.hkjcfootball.com');
    const isHkjcRacingSite =
        window.globalConfig.IS_EWIN2 &&
        (ewin2NonProdDomains.includes(window.location.host) || window.location.host === 'logon.hkjcracing.com');

    if (window.BetLiteSite) return '2425LITE';
    if (isMassSite) return 'ewin-bet';
    if (isSpeedBetSite) return 'ewin-speedbet';
    if (isHkjcFootballSite) return 'ewin-hkjcfootball';
    if (isHkjcRacingSite) return 'ewin-hkjcracing';
};

export const useAdobeLaunch = () => {
    const { i18n } = useTranslation();

    const sendAdobeEvent = useCallback(
        /**
         * @typedef {{
         *   event: "pageview";
         *   internal_ad: string;
         *   content?: {
         *     category: string;
         *     race_no?: string;
         *   };
         *   user?: {
         *      preference: string;
         *   };
         * } | {
         *   event: "interaction";
         *   interaction?: {
         *     eventID?: string;
         *     download_file_url?: string;
         *   };
         *   content?: {
         *     category: string;
         *     race_no?: string;
         *     betType?: string;
         *   };
         *   user?: {
         *      preference: string;
         *   };
         * }} Data
         * @param {Data} data
         */
        (data) =>
            window.adobeDataLayer.push(
                merge(
                    {
                        page: { id: getPageId(), language: i18n.language === 'ch' ? 'zh-hk' : 'en-us' },
                        user: { cuno: getWebIDHashed(), customer_segment: getCustomerSegment(), pcard_tier: getPCardTier() }
                    },
                    data
                )
            ),
        [i18n.language]
    );

    return { adobeDataLayer: window.adobeDataLayer, sendAdobeEvent };
};

export const TrackPageView = memo(() => {
    const location = useLocation();
    const { sendAdobeEvent } = useAdobeLaunch();
const debounceSendAdobeEvent = useMemo(() => debounce(sendAdobeEvent, 1500), [sendAdobeEvent]);

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const pathnameParts = location.pathname.split('/');

        // use debounce here to avoid sending page view events for pages being auto redirected away
        debounceSendAdobeEvent({
            event: 'pageview',
            internal_ad: searchParams.get('b_cid'),
            // special handling for race sim pages view tracking
            content: {
                category: searchParams.get('mode'), // race sim mode, Simple | Advanced
                // {YYYYMMDD}-{venue}-{raceNo}
                // TrackPageView is place at the root, so useParams() will return empty
                race_no: /\/horse_selector\/\d{4}-\d{2}-\d{2}\/[^/]+\/\d+$/.test(location.pathname) // attempt to match /horse_selector/:YYYY-MM-DD/:venue/:raceNo
                    ? `${pathnameParts.at(-3)?.replaceAll('-', '')}-${pathnameParts.at(-2)}-${pathnameParts.at(-1)}`
                    : /\/race-sim\/\d{4}-\d{2}-\d{2}\/[^/]+\/\d+\/[^/]+$/.test(location.pathname) // attempt to match /race-sim/:YYYY-MM-DD/:venue/:raceNo/:raceData, i.e. webgl animation pop up
                      ? `${pathnameParts.at(-4)?.replaceAll('-', '')}-${pathnameParts.at(-3)}-${pathnameParts.at(-2)}`
                      : undefined
            },
            user: {
                preference: searchParams.get('userPreference') // e.g. VID:Y/N for race sim
            }
        });
    }, [debounceSendAdobeEvent, location.pathname, location.search, location.hash]);

    return null;
});

TrackPageView.displayName = 'TrackPageView';
